import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button, DropdownMenu, Spinner } from '../common';
import { Pager, PageInfo, PagerBlock, PagerHandle } from './Pager';
import { ReaderAnchor, ReaderSelection } from '../../lib/reader/selection';
import { ReaderChapter } from '../../lib/reader/types';
import { anchorOfWordOffset, wordOffsetOfAnchor } from '../../lib/reader/words';
import { MultiSelection, ReaderTap, useReaderSelection } from '../../hooks/useReaderSelection';

export type { ReaderChapter } from '../../lib/reader/types';
export type { ReaderTap } from '../../hooks/useReaderSelection';

// The reading cursor handed to the parent for backend persistence. `blockId` is
// the data-reader-seg of the word at the top of the page; `wordOffset` is its
// resolution-independent position within the chapter.
export interface ReaderPosition {
  chapterId: string;
  blockId: string;
  charOffset: number;
  wordOffset: number;
}

// A standardized centered surface the parent asks Reader to draw when the
// current chapter isn't ready. `offer` adds an action + an "enable auto…" toggle
// (used on the first not-yet-translated / not-yet-generated chapter).
export interface ReaderOverlay {
  kind: 'spinner' | 'hint' | 'offer';
  message: string;
  // Optional plan preview (scene plan points) shown above the actions.
  planPoints?: string[];
  primaryLabel?: string;
  onPrimary?: () => void;
  toggleLabel?: string;
  onToggle?: () => void;
}

export interface ReaderHandle {
  scrollToAnchor(anchor: ReaderAnchor): boolean;
  getAnchor(): ReaderAnchor | null;
  turnPage(dir: 1 | -1): boolean;
  goToChapter(idx: number, landOn?: 'first' | 'last'): void;
  clearSelection(): void;
}

interface ReaderProps {
  chapters: ReaderChapter[];
  bookTitle: string;
  loading?: boolean;
  // Breadcrumb shown above the reader (hidden in fullscreen).
  breadcrumb?: ReactNode;
  // Back arrow target when not fullscreen (in fullscreen the arrow exits it).
  onExit?: () => void;
  // Backend reading position, used when the URL has no ?ch.
  initialPosition?: { blockId: string; charOffset: number } | null;
  onPersistPosition?: (p: ReaderPosition) => void;
  // Observe the current chapter (so the parent can run its auto-fill policy).
  onChapterChange?: (index: number) => void;
  // A finalised single-paragraph selection — the parent opens its own sheet.
  onSelect?: (sel: ReaderSelection) => void;
  // A tap that did not become a selection. Return true if you handled it (e.g.
  // opened a menu) to stop Reader from toggling its immersive chrome.
  onTap?: (tap: ReaderTap) => boolean | void;
  // Suppress selection while the parent's sheet is open.
  selectionLocked?: boolean;
  // Title used by the built-in multi-selection share button.
  shareTitle: string;
  headerActions?: ReactNode;
  // Actions placed in the footer chrome, beside the page counter (e.g. a
  // "Scene" button that opens the current scene's tools).
  footerActions?: ReactNode;
  // Replaces the entire footer (prev/next page nav + counter) — used by audio
  // mode to show narration transport controls instead of page turning.
  footerOverride?: ReactNode;
  moreMenuItems?: ReactNode;
  overlay?: ReaderOverlay | null;
  // Set true while a parent-owned header dropdown is open, so the fullscreen
  // chrome won't auto-hide mid-action.
  chromeBusy?: boolean;
  // Keep the fullscreen chrome permanently visible (never auto-hide) — used by
  // audio mode so the transport controls stay reachable.
  chromePinned?: boolean;
  contentClassName?: string;
}

const DEFAULT_CONTENT_CLASS =
  'font-serif text-[1.075rem] leading-8 py-6 [&>*]:mb-5 text-justify hyphens-auto text-slate-900 dark:text-slate-100 sm:text-[1.15rem]';

export const Reader = forwardRef<ReaderHandle, ReaderProps>(function Reader(
  {
    chapters,
    bookTitle,
    loading,
    breadcrumb,
    onExit,
    initialPosition,
    onPersistPosition,
    onChapterChange,
    onSelect,
    onTap,
    selectionLocked,
    shareTitle,
    headerActions,
    footerActions,
    footerOverride,
    moreMenuItems,
    overlay,
    chromeBusy,
    chromePinned,
    contentClassName,
  },
  ref
) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fullscreen lives in the URL (?fullscreen=1) so it deep-links and survives a
  // refresh.
  const fullscreen = searchParams.get('fullscreen') === '1';
  const setFullscreen = (on: boolean) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set('fullscreen', '1');
        else next.delete('fullscreen');
        return next;
      },
      { replace: true }
    );

  const [chapterIndex, setChapterIndex] = useState(0);
  const [pageInfo, setPageInfo] = useState({ page: 0, pageCount: 1 });
  const [selection, setSelection] = useState<ReaderSelection | null>(null);
  const [multiSelection, setMultiSelection] = useState<MultiSelection | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // In fullscreen the header/footer hide for an immersive view; a short tap on
  // the text toggles them back. Non-fullscreen always shows them.
  const [chromeShown, setChromeShown] = useState(false);

  const pagerRef = useRef<PagerHandle>(null);
  // Restore runs once: pick the chapter, then scroll its saved word to the top.
  // Persistence stays off until the restore has settled so we never overwrite
  // the saved position with the chapter's top on first paint.
  const restoreDecidedRef = useRef(false);
  const restoreAnchorRef = useRef<ReaderAnchor | null>(null);
  const restorePendingRef = useRef(false);
  const restoredRef = useRef(false);
  // A chapter we entered from its end (paging backwards) lands on its last page.
  const pendingLastRef = useRef(false);
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentChapter = chapters[chapterIndex];

  // Latest chapters/index, readable from the debounced persist closure.
  const chaptersRef = useRef(chapters);
  chaptersRef.current = chapters;
  const chapterIndexRef = useRef(chapterIndex);
  chapterIndexRef.current = chapterIndex;

  useEffect(() => {
    onChapterChange?.(chapterIndex);
  }, [chapterIndex, onChapterChange]);

  // Only the current chapter is rendered, so the paginator measures a handful of
  // pages instead of the whole book — this keeps the reader responsive.
  const blocks: PagerBlock[] = useMemo(() => {
    if (!currentChapter) return [];
    const result: PagerBlock[] = [
      {
        key: `ch-${currentChapter.id}`,
        node: (
          <h2 className="mb-4 font-sans text-xl font-semibold text-slate-900 dark:text-slate-100">
            {currentChapter.title}
          </h2>
        ),
      },
    ];
    currentChapter.blocks.forEach((b) => result.push({ key: b.id, node: b.node }));
    return result;
  }, [currentChapter]);

  // --- selection ---
  const selectionHook = useReaderSelection({
    enabled: !!currentChapter && !selection && !multiSelection && !selectionLocked,
    onSelect: (sel) => {
      setSelection(sel);
      // Selecting a phrase also marks your place (immediately, not debounced).
      const chapter = chaptersRef.current[chapterIndexRef.current];
      if (chapter && restoredRef.current) {
        const anchor: ReaderAnchor = { segmentId: sel.segmentId, charOffset: sel.start };
        onPersistPosition?.({
          chapterId: chapter.id,
          blockId: anchor.segmentId,
          charOffset: anchor.charOffset,
          wordOffset: wordOffsetOfAnchor(chapter.blocks, anchor),
        });
      }
      onSelect?.(sel);
    },
    onSelectMulti: setMultiSelection,
    onTap: (tap) => {
      const handled = onTap?.(tap);
      if (!handled && fullscreen) setChromeShown((v) => !v);
    },
  });

  const clearSelection = useCallback(() => {
    selectionHook.clear();
    setSelection(null);
    setMultiSelection(null);
  }, [selectionHook]);

  // --- fullscreen immersive chrome ---
  // Pinned chrome (e.g. audio mode) stays up; otherwise it hides on entering
  // fullscreen and a tap brings it back.
  useEffect(() => {
    if (fullscreen) setChromeShown(chromePinned ?? false);
  }, [fullscreen, chromePinned]);

  const CHROME_IDLE_MS = 3500;
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chromeHoverRef = useRef(false);
  const armChromeHide = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
    if (!fullscreen || chromeHoverRef.current || navOpen || moreOpen || chromeBusy || chromePinned)
      return;
    hideTimerRef.current = setTimeout(() => setChromeShown(false), CHROME_IDLE_MS);
  }, [fullscreen, navOpen, moreOpen, chromeBusy, chromePinned]);

  useEffect(() => {
    if (fullscreen && chromeShown) armChromeHide();
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [fullscreen, chromeShown, armChromeHide]);

  // --- restore once the chapters load ---
  useEffect(() => {
    if (restoreDecidedRef.current || chapters.length === 0) return;
    restoreDecidedRef.current = true;
    const urlCh = searchParams.get('ch');
    const urlW = searchParams.get('w');
    let ci = -1;
    let anchor: ReaderAnchor | null = null;
    if (urlCh) {
      ci = chapters.findIndex((c) => c.id === urlCh);
      if (ci >= 0) anchor = anchorOfWordOffset(chapters[ci].blocks, Number(urlW) || 0);
    }
    if (ci < 0 && initialPosition) {
      ci = chapters.findIndex((c) => c.blocks.some((b) => b.id === initialPosition.blockId));
      if (ci >= 0)
        anchor = { segmentId: initialPosition.blockId, charOffset: initialPosition.charOffset };
    }
    // At the very start of a chapter (w=0) keep the scroll at the top so the
    // chapter heading shows, rather than scrolling the first word under it.
    const firstBlock = ci >= 0 ? chapters[ci].blocks[0]?.id : undefined;
    if (anchor && anchor.segmentId === firstBlock && anchor.charOffset <= 0) anchor = null;
    restorePendingRef.current = true;
    restoreAnchorRef.current = anchor;
    if (ci > 0) setChapterIndex(ci);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapters]);

  // Apply the one-shot restore once the target chapter is laid out.
  useEffect(() => {
    if (!restorePendingRef.current) return;
    const a = restoreAnchorRef.current;
    if (a && !pagerRef.current?.scrollToAnchor(a)) return; // not laid out yet
    restorePendingRef.current = false;
    restoredRef.current = true;
  }, [chapterIndex, blocks]);

  // --- persist (debounced) to URL ?ch&w + backend ---
  const persistPosition = useCallback(
    (anchor: ReaderAnchor | null) => {
      if (!restoredRef.current) return;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        const chapter = chaptersRef.current[chapterIndexRef.current];
        if (!chapter) return;
        const wordOffset = anchor ? wordOffsetOfAnchor(chapter.blocks, anchor) : 0;
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('ch', chapter.id);
            next.set('w', String(wordOffset));
            return next;
          },
          { replace: true }
        );
        if (anchor) {
          onPersistPosition?.({
            chapterId: chapter.id,
            blockId: anchor.segmentId,
            charOffset: anchor.charOffset,
            wordOffset,
          });
        }
      }, 350);
    },
    // setSearchParams / onPersistPosition are stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  const onPosition = useCallback(
    (info: PageInfo) => {
      setPageInfo((prev) =>
        prev.page === info.page && prev.pageCount === info.pageCount
          ? prev
          : { page: info.page, pageCount: info.pageCount }
      );
      if (restorePendingRef.current) return; // the restore effect owns the scroll
      if (pendingLastRef.current) {
        pendingLastRef.current = false;
        pagerRef.current?.scrollToBottom();
        return;
      }
      persistPosition(info.anchor);
    },
    [persistPosition]
  );

  useEffect(
    () => () => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    },
    []
  );

  // --- chapter navigation + page turns ---
  const goToChapter = useCallback((idx: number, landOn: 'first' | 'last' = 'first') => {
    if (idx < 0 || idx >= chaptersRef.current.length) return;
    pendingLastRef.current = landOn === 'last';
    setChapterIndex(idx);
    setNavOpen(false);
  }, []);

  const nextPage = useCallback(() => {
    if (pagerRef.current?.turnPage(1)) return;
    if (chapterIndexRef.current < chaptersRef.current.length - 1)
      goToChapter(chapterIndexRef.current + 1);
  }, [goToChapter]);
  const prevPage = useCallback(() => {
    if (pagerRef.current?.turnPage(-1)) return;
    if (chapterIndexRef.current > 0) goToChapter(chapterIndexRef.current - 1, 'last');
  }, [goToChapter]);
  const nextPageRef = useRef(nextPage);
  const prevPageRef = useRef(prevPage);
  nextPageRef.current = nextPage;
  prevPageRef.current = prevPage;

  // Keyboard: Esc closes the menu then leaves fullscreen; left/PageUp and
  // right/PageDown turn the page. Up/Down scroll (left to the browser). Must be a
  // window listener — the mobile shell dispatches Arrow keys for volume paging.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
      if (e.key === 'Escape') {
        if (navOpen) setNavOpen(false);
        else if (fullscreen && !selection) setFullscreen(false);
        return;
      }
      if (typing || selection || navOpen) return;
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        e.preventDefault();
        nextPageRef.current();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prevPageRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navOpen, fullscreen, selection]);

  useImperativeHandle(
    ref,
    (): ReaderHandle => ({
      scrollToAnchor: (a) => pagerRef.current?.scrollToAnchor(a) ?? false,
      getAnchor: () => pagerRef.current?.getAnchor() ?? null,
      turnPage: (dir) => {
        if (pagerRef.current?.turnPage(dir)) return true;
        if (dir > 0 && chapterIndexRef.current < chaptersRef.current.length - 1) {
          goToChapter(chapterIndexRef.current + 1);
          return true;
        }
        if (dir < 0 && chapterIndexRef.current > 0) {
          goToChapter(chapterIndexRef.current - 1, 'last');
          return true;
        }
        return false;
      },
      goToChapter,
      clearSelection,
    }),
    [goToChapter, clearSelection]
  );

  const { page, pageCount } = pageInfo;

  const shareSelection = (sel: { text: string }) => {
    if (typeof navigator.share === 'function') {
      navigator.share({ title: `“${shareTitle}”`, text: sel.text }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(sel.text).catch(() => {});
    }
  };

  const closeMulti = () => {
    selectionHook.clear();
    setMultiSelection(null);
  };

  if (loading) {
    return (
      <div className="grid min-h-[50vh] place-items-center">
        <Spinner />
      </div>
    );
  }

  const containerClass = fullscreen
    ? 'fixed inset-0 z-40 flex flex-col bg-white dark:bg-gray-900'
    : 'relative flex h-[calc(100dvh-9rem)] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900';
  const chromeAnim = 'transition-[transform,opacity] duration-300 ease-out';
  const showChrome = chromeShown || !!chromePinned;
  const headerClass = [
    'flex items-center gap-1 border-b border-slate-200 px-2 py-2 dark:border-gray-700 sm:gap-2 sm:px-4 sm:py-3',
    fullscreen
      ? `absolute inset-x-0 top-0 z-30 bg-white/75 backdrop-blur-sm ${chromeAnim} dark:bg-gray-900/75 ${
          showChrome
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none -translate-y-full opacity-0'
        }`
      : '',
  ].join(' ');
  const footerClass = [
    'flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2.5 text-sm dark:border-gray-700 sm:px-4 sm:py-3',
    fullscreen
      ? `absolute inset-x-0 bottom-0 z-30 bg-white/75 backdrop-blur-sm ${chromeAnim} dark:bg-gray-900/75 ${
          showChrome
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-full opacity-0'
        }`
      : '',
  ].join(' ');

  return (
    <div style={{ userSelect: 'none' }}>
      {!fullscreen && breadcrumb}

      <div className={containerClass}>
        <header
          className={headerClass}
          onMouseEnter={() => {
            chromeHoverRef.current = true;
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
          }}
          onMouseLeave={() => {
            chromeHoverRef.current = false;
            armChromeHide();
          }}
        >
          <button
            type="button"
            onClick={() => (fullscreen ? setFullscreen(false) : onExit?.())}
            title={
              fullscreen ? t('common.exitFullScreen', 'Exit full screen') : t('common.back', 'Back')
            }
            className="shrink-0 px-1 text-lg text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100"
          >
            ←
          </button>
          <div className="relative min-w-0 flex-1">
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              title={t('reader.contents', 'Contents')}
              className="flex w-full min-w-0 items-baseline gap-2 text-left"
            >
              <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                {bookTitle}
              </span>
              {currentChapter && (
                <span className="hidden shrink-0 truncate text-xs text-slate-400 sm:inline">
                  {currentChapter.title}
                </span>
              )}
            </button>
            {navOpen && (
              <>
                <button
                  aria-label={t('common.close', 'Close')}
                  className="fixed inset-0 z-10 cursor-default"
                  onClick={() => setNavOpen(false)}
                />
                <nav className="absolute left-0 top-full z-20 mt-1 max-h-72 w-72 max-w-[80vw] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                  {chapters.map((ch, i) => (
                    <button
                      key={ch.id}
                      onClick={() => goToChapter(i)}
                      className={`block w-full px-4 py-2 text-left text-sm ${
                        i === chapterIndex
                          ? 'bg-slate-100 font-medium text-slate-900 dark:bg-gray-700 dark:text-slate-100'
                          : 'text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      {ch.title}
                    </button>
                  ))}
                </nav>
              </>
            )}
          </div>

          {headerActions}

          <button
            type="button"
            onClick={() => setFullscreen(!fullscreen)}
            title={
              fullscreen
                ? t('common.exitFullScreen', 'Exit full screen')
                : t('common.fullScreen', 'Full screen')
            }
            className="shrink-0 rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-700"
          >
            {fullscreen ? (
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
          {moreMenuItems && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                title={t('reader.more', 'More')}
                className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-gray-700"
              >
                ⋯
              </button>
              <DropdownMenu isOpen={moreOpen} onClose={() => setMoreOpen(false)} position="right">
                <div onClick={() => setMoreOpen(false)}>{moreMenuItems}</div>
              </DropdownMenu>
            </div>
          )}
        </header>

        <Pager
          key={currentChapter?.id}
          ref={pagerRef}
          blocks={blocks}
          onPosition={onPosition}
          contentClassName={contentClassName ?? DEFAULT_CONTENT_CLASS}
          onSwipeLeft={nextPage}
          onSwipeRight={prevPage}
          suppressSwipe={selectionHook.isSelecting}
        />

        {overlay && (
          <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 flex-col items-center gap-3 px-6 text-center text-slate-500">
            {overlay.kind === 'spinner' && <Spinner />}
            <span className={overlay.kind === 'hint' ? 'text-sm text-slate-400' : 'text-sm'}>
              {overlay.message}
            </span>
            {overlay.planPoints && overlay.planPoints.length > 0 && (
              <ul className="mx-auto max-w-md space-y-1 text-left text-sm text-slate-600 dark:text-slate-300">
                {overlay.planPoints.map((point, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="select-none text-slate-400">•</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            )}
            {overlay.kind === 'offer' && (
              <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-2">
                {overlay.primaryLabel && (
                  <Button size="sm" onClick={overlay.onPrimary}>
                    {overlay.primaryLabel}
                  </Button>
                )}
                {overlay.toggleLabel && (
                  <Button size="sm" variant="secondary" onClick={overlay.onToggle}>
                    {overlay.toggleLabel}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <footer
          className={footerClass}
          onMouseEnter={() => {
            chromeHoverRef.current = true;
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
          }}
          onMouseLeave={() => {
            chromeHoverRef.current = false;
            armChromeHide();
          }}
        >
          {footerOverride ?? (
            <>
              <button
                type="button"
                onClick={prevPage}
                disabled={chapterIndex === 0 && page <= 0}
                className="rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-gray-700 sm:px-3"
              >
                ← {t('reader.prev', 'Prev')}
              </button>
              <div className="flex min-w-0 items-center gap-2">
                <span className="shrink-0 text-xs text-slate-400">
                  {chapters.length > 1 ? `${chapterIndex + 1}/${chapters.length} · ` : ''}
                  {Math.min(page + 1, pageCount)}/{pageCount}
                </span>
                {footerActions}
              </div>
              <button
                type="button"
                onClick={nextPage}
                disabled={chapterIndex >= chapters.length - 1 && page >= pageCount - 1}
                className="rounded-lg px-2 py-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-gray-700 sm:px-3"
              >
                {t('reader.next', 'Next')} →
              </button>
            </>
          )}
        </footer>
      </div>

      {/* A selection spanning several paragraphs can't be reworded — offer
          copy/share instead, anchored near where the selection ends. */}
      {multiSelection && (
        <>
          <button
            type="button"
            aria-label={t('common.close', 'Close')}
            className="fixed inset-0 z-40 cursor-default"
            onClick={closeMulti}
          />
          <div
            className="fixed z-50 flex -translate-x-1/2 items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
            style={{
              left: Math.min(
                window.innerWidth - 100,
                Math.max(100, multiSelection.rect.left + multiSelection.rect.width / 2)
              ),
              top: Math.max(56, multiSelection.rect.top - 52),
            }}
          >
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                navigator.clipboard?.writeText(multiSelection.text).catch(() => {});
                closeMulti();
              }}
            >
              {t('common.copy', 'Copy')}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                shareSelection(multiSelection);
                closeMulti();
              }}
            >
              {t('reader.share', 'Share')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
});
